import {type UpmPlantsHierarchyNode, collectPlantIds} from '@domain/upm'
import {TimeZone} from '@hconnect/uikit'
import {
  defaultQuickSelectionItems,
  extendedQuickSelectionItems,
  EXTENDED_QUICK_SELECTION
} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import {Box} from '@mui/material'
import {AddFilterButton} from '@widgets/shift-event-filters'
import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {TasksFilterKey, TasksFilterKeyName, FEATURE_NAMESPACE} from '../../consts'
import {type TasksFiltersState} from '../../types'
import {isTasksFilterKey} from '../../utils'

import {
  createAreaFilter,
  createAssigneeFilter,
  createCategoriesFilter,
  createCreatedByFilter,
  createDateRangeFilter,
  createEquipmentFilter,
  createMainEquipmentFilter,
  createPlantFilter,
  createPomActionSourceFilter,
  createPomCostLeadershipFilter,
  createPomQuestionCategoryFilter,
  createPomQuestionFilter,
  createPomSectionFilter,
  createPomTopicFilter,
  createPrioritiesFilter,
  createProcessStagesFilter,
  createStatusesFilter,
  createTextFilter,
  createCriticalityFilter,
  TaskFilterConfig,
  TasksFiltersStateValue
} from './taskFiltersConfig'

const plantRequiringFilters: TasksFilterKeyName[] = [
  TasksFilterKey.PROCESS_STAGES,
  TasksFilterKey.MAIN_EQUIPMENT,
  TasksFilterKey.EQUIPMENT,
  TasksFilterKey.OIL_ANALYSIS_CRITICALITY
]

type Props = {
  filters: TasksFiltersState
  onFiltersChange: (filters: TasksFiltersState) => void
  timezone: TimeZone
  hierarchyData?: UpmPlantsHierarchyNode
}

export const TasksFilters: React.FC<Props> = ({
  filters,
  onFiltersChange,
  timezone,
  hierarchyData
}) => {
  const [visibleFilters, setVisibleFilters] = React.useState<TasksFilterKeyName[]>(
    getInitialVisibleFilters(filters)
  )
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const selectedAreaNode = useMemo(() => {
    if (!filters.area || !hierarchyData) return undefined
    return hierarchyData.children.find((node) => node.hierarchyId === filters.area)
  }, [filters.area, hierarchyData])

  const allowedPlantUpmIds = useMemo(
    () => selectedAreaNode && collectPlantIds(selectedAreaNode),
    [selectedAreaNode]
  )

  const handleFilterChange = (key: TasksFilterKeyName, value?: TasksFiltersStateValue) => {
    onFiltersChange({...filters, [key]: value})
  }

  const clearFilter = (key: TasksFilterKeyName) => {
    onFiltersChange({...filters, [key]: undefined})
    setVisibleFilters(visibleFilters.filter((filter) => filter !== key))
  }

  const createHandleFilterWithDependantFieldsChange =
    (filtersToClear?: TasksFilterKeyName[]) =>
    (key: TasksFilterKeyName, value?: TasksFiltersStateValue) => {
      const newFilters = {...filters}
      filtersToClear?.forEach((filter) => {
        newFilters[filter] = undefined
      })
      onFiltersChange({...newFilters, [key]: value})
    }

  const handlePlantFilterChange = createHandleFilterWithDependantFieldsChange(plantRequiringFilters)

  const handlePlantFilterClear = () => {
    handlePlantFilterChange(TasksFilterKey.PLANT, undefined)
    setVisibleFilters(
      visibleFilters
        .filter((filter) => filter !== TasksFilterKey.PLANT)
        .filter((filter) => !plantRequiringFilters.includes(filter))
    )
  }

  const handleAreaFilterChange = useCallback(
    (key: TasksFilterKeyName, value?: TasksFiltersStateValue) => {
      const newFilters: TasksFiltersState = {
        ...filters,
        [key]: value,
        [TasksFilterKey.PLANT]: undefined
      }
      plantRequiringFilters.forEach((filter) => {
        newFilters[filter] = undefined
      })
      onFiltersChange(newFilters)
    },
    [filters, onFiltersChange]
  )

  const handleAreaFilterClear = useCallback(() => {
    onFiltersChange({
      ...filters,
      [TasksFilterKey.AREA]: undefined
    })
    setVisibleFilters((prev) => prev.filter((filterName) => filterName !== TasksFilterKey.AREA))
  }, [filters, onFiltersChange])

  const handleVisibleFiltersChange = (value: TasksFilterKeyName[]) => {
    setVisibleFilters((prevFilters) => {
      const newVisibleFilters = !value.includes(TasksFilterKey.PLANT)
        ? value.filter((filter) => !plantRequiringFilters.includes(filter))
        : value
      const filtersToClear = prevFilters.filter((filter) => !newVisibleFilters.includes(filter))
      const newFilters = {...filters}

      filtersToClear.forEach((filter) => {
        newFilters[filter] = undefined
      })

      if (
        newVisibleFilters.includes(TasksFilterKey.CREATED_DATE_RANGE) &&
        !prevFilters.includes(TasksFilterKey.CREATED_DATE_RANGE)
      ) {
        newFilters[TasksFilterKey.CREATED_DATE_RANGE] = EXTENDED_QUICK_SELECTION.LAST_YEAR
      }

      if (
        newVisibleFilters.includes(TasksFilterKey.POM_COST_LEADERSHIP) &&
        !prevFilters.includes(TasksFilterKey.POM_COST_LEADERSHIP)
      ) {
        newFilters[TasksFilterKey.POM_COST_LEADERSHIP] = 'true'
      }

      onFiltersChange(newFilters)

      return newVisibleFilters
    })
  }

  const filterBaseConfig = {
    filters,
    onChange: handleFilterChange,
    clearFilter,
    visibleFilters
  }

  const filtersConfig: TaskFilterConfig[] = [
    createDateRangeFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.CREATED_DATE_RANGE,
      label: t('shiftEvent.label.createdDate'),
      timezone,
      selectionItems: extendedQuickSelectionItems(t, timezone)
    }),
    createTextFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.TITLE,
      label: t('shiftEvent.label.title')
    }),
    createDateRangeFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.DUE_DATE_RANGE,
      label: t('shiftEvent.label.dueDate'),
      timezone,
      selectionItems: defaultQuickSelectionItems(t, timezone)
    }),
    createStatusesFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.STATUSES,
      label: t('shiftEvent.label.status')
    }),
    createAssigneeFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.ASSIGNEE,
      label: t('shiftEvent.label.assignee')
    }),
    createCreatedByFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.CREATED_BY,
      label: t('shiftEvent.label.createdBy')
    }),
    createCategoriesFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.CATEGORIES,
      label: t('shiftEvent.label.category')
    }),
    createPrioritiesFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.PRIORITIES,
      label: t('shiftEvent.label.priority')
    }),
    createPomActionSourceFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.POM_ACTION_SOURCES,
      label: t('pomAction.label.source')
    }),
    createPomCostLeadershipFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.POM_COST_LEADERSHIP,
      label: t('pomAction.label.costLeadership')
    }),
    createPomSectionFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.POM_SECTIONS,
      label: t('pomAction.label.section'),
      onChange: createHandleFilterWithDependantFieldsChange([
        TasksFilterKey.POM_TOPICS,
        TasksFilterKey.POM_QUESTIONS
      ])
    }),
    createPomTopicFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.POM_TOPICS,
      label: t('pomAction.label.topic'),
      onChange: createHandleFilterWithDependantFieldsChange([TasksFilterKey.POM_QUESTIONS])
    }),
    createPomQuestionFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.POM_QUESTIONS,
      label: t('pomAction.label.question')
    }),
    createPomQuestionCategoryFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.POM_QUESTION_CATEGORIES,
      label: t('pomAction.label.questionCategory')
    }),
    createAreaFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.AREA,
      label: t('tasksTable.label.area'),
      onChange: handleAreaFilterChange,
      clearFilter: handleAreaFilterClear,
      hierarchyData
    }),
    createPlantFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.PLANT,
      label: t('tasksTable.label.plant'),
      onChange: handlePlantFilterChange,
      clearFilter: handlePlantFilterClear,
      allowedPlantUpmIds
    }),
    createProcessStagesFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.PROCESS_STAGES,
      label: t('shiftEvent.label.processStage')
    }),
    createMainEquipmentFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.MAIN_EQUIPMENT,
      label: t('shiftEvent.label.mainEquipment')
    }),
    createEquipmentFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.EQUIPMENT,
      label: t('shiftEvent.label.equipment')
    }),
    createCriticalityFilter({
      ...filterBaseConfig,
      key: TasksFilterKey.OIL_ANALYSIS_CRITICALITY,
      label: t('task.label.criticality')
    })
  ]

  return (
    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
      {filtersConfig
        .filter((option) => visibleFilters.includes(option.value))
        .map((option) => (
          <React.Fragment key={option.value}>{option.filter}</React.Fragment>
        ))}
      <Box sx={{textWrap: 'nowrap'}}>
        <AddFilterButton
          data-test-id={'tasks-add-filter'}
          options={filtersConfig}
          onChange={handleVisibleFiltersChange}
          values={visibleFilters}
          buttonLabel={t('shiftEvent.label.addFilter')}
        />
      </Box>
    </Box>
  )
}

const getInitialVisibleFilters = (filters: TasksFiltersState): TasksFilterKeyName[] => {
  return Object.keys(filters).filter(isTasksFilterKey)
}
